/**
 * Personalised campaign — generate, review, send.
 *
 * The flow is deliberately manual in the middle: images are generated in the
 * background (one at a time, by the backend job), then a human looks at each
 * one and picks what goes out. Nothing is ever sent without being selected
 * here, because an AI-made image with a wrong logo is worse than no email.
 *
 * While the campaign is generating the screen refreshes itself every 5 seconds;
 * the timer is dropped as soon as generation ends or the view is destroyed.
 *
 * Proofsheet button: the campaign may carry a product configuration, picked
 * with the platform's own product configurator. A recipient who clicks
 * {{proofsheet_url}} in the email and confirms gets an offer with it, their
 * image as its custom proofsheet; the row then links to that offer.
 *
 * Keyboard: Escape closes whichever dialog is open; in the image preview the
 * left / right arrows step through the recipients that have an image, in the
 * order the table is currently sorted. The listener sits on the document (the
 * dialogs hold no focusable element to attach it to) and goes with the view.
 */
define('mailingPersonalisedReviewView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'mailingProofsheetProduct',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  ProofsheetProduct,
  MailingApi
) {
  'use strict';

  var POLL_MS = 5000;
  var STALL_SECONDS = 150;

  var STATUS_CLASS = {
    DRAFT: 'draft',
    GENERATING: 'scheduled',
    REVIEW: 'review',
    SENDING: 'sending',
    DONE: 'sent'
  };

  var GENERATION_LABEL = {
    PENDING: 'Pending',
    GENERATING: 'Generating…',
    DONE: 'Ready',
    FAILED: 'Failed'
  };

  var ReviewView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-personalised-review',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading campaign…</div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-back': 'onBack',
      'click .mlg-refresh': 'load',
      'click .mlg-generate': 'onGenerate',
      'click .mlg-test-image': 'onOpenTest',
      'click .mlg-show-queue': 'onShowQueue',
      'click .mlg-edit-schedule': 'onEditSchedule',
      'click .mlg-schedule-save': 'onSaveSchedule',
      'click .mlg-queue-remove': 'onQueueRemove',
      'click .mlg-queue-clear': 'onQueueClear',
      'click .mlg-test-run': 'onRunTest',
      'keydown .f-test-url': 'onTestKey',
      'click .mlg-send-selected': 'onSend',
      'change .mlg-select-all': 'onSelectAll',
      'change .mlg-select-item': 'onSelectItem',
      'click .mlg-item-image': 'onShowImage',
      'click .mlg-image-prev': 'onPrevImage',
      'click .mlg-image-next': 'onNextImage',
      'click .mlg-item-email': 'onShowEmail',
      'click .mlg-item-remove': 'onRemove',
      'click .mlg-items th[data-sort]': 'onSort',
      'click .mlg-item-regenerate': 'onRegenerate',
      'click .mlg-item-retry': 'onRetry',
      'click .mlg-retry-failed': 'onRetryAllFailed',
      'click .mlg-proofsheet-choose': 'onChooseProofsheetProduct',
      'click .mlg-image-prompt-edit': 'onEditImagePrompt',
      'click .mlg-image-prompt-save': 'onSaveImagePrompt',
      'click .mlg-regenerate-older': 'onRegenerateOlderPrompt',
      'click .mlg-fix-multi-address': 'onFixMultiAddress',
      'click .mlg-add-new-contacts': 'onAddNewContacts',
      'click .mlg-proofsheet-remove': 'onRemoveProofsheetProduct',
      'click .mlg-proofsheet-open': 'onOpenOffer',
      'click .mlg-modal-cancel': 'closeModal'
    },

    initialize: function (options) {
      this.secId = options.secId;
      this.selected = {};
      this.loggedFailures = {};
      this.sort = { key: 'id', dir: 1 };
      this.onKeyDown = _.bind(this.onKeyDown, this);
      $(document).on('keydown', this.onKeyDown);
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('personalised',
          '<button type="button" class="mlg-btn mlg-refresh">Refresh</button>' +
          '<a class="mlg-btn mlg-back" href="#mailing/personalised">Back to list</a>')
      };
    },

    onShow: function () {
      this.load();
      this.loadNewContacts();
    },

    /**
     * Contacts of the segment not yet in the campaign. Its own call, once per
     * visit and after an addition: it runs the whole segment, too heavy for the
     * polling that follows a generation.
     */
    loadNewContacts: function () {
      var self = this;
      MailingApi.personalisedNewContacts(this.secId).done(function (result) {
        if (self.isDestroyed) { return; }
        self.newContacts = (result || {}).count || 0;
        if (self.campaign) { self.renderAll(); }
      });
    },

    onAddNewContacts: function () {
      var count = this.newContacts || 0;
      if (!window.confirm('Add the ' + count + ' contact(s) that joined the segment since the campaign started?\n\n' +
          'One image is generated for each (one paid OpenAI call each), with the current brief and prompt. ' +
          'Existing recipients are not touched and nothing is sent: select them and send once their images are ready.')) {
        return;
      }
      var self = this;
      MailingApi.addPersonalisedNewContacts(this.secId).done(function (result) {
        Shell.banner(self.$el, (result.added || 0) + ' new contact(s) added — their images are being generated', 'ok');
        self.newContacts = 0;
        self.load();
        self.loadNewContacts();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onDestroy: function () {
      this.stopPolling();
      $(document).off('keydown', this.onKeyDown);
    },

    onKeyDown: function (e) {
      if (!this.ui.modal.is(':visible')) { return; }
      if (e.which === 27) {                       // Escape
        e.preventDefault();
        this.closeModal();
      } else if (this.imageIndex !== undefined) {  // arrows, image preview only
        if (e.which === 37) { e.preventDefault(); this.showImageAt(this.imageIndex - 1); }
        if (e.which === 39) { e.preventDefault(); this.showImageAt(this.imageIndex + 1); }
      }
    },

    load: function () {
      var self = this;
      $.when(
        MailingApi.personalisedCampaign(this.secId),
        MailingApi.personalisedItems(this.secId),
        MailingApi.personalisedStats(this.secId)
      ).done(function (campaign, items, stats) {
        if (self.isDestroyed) { return; }
        self.campaign = campaign;
        self.items = items || [];
        self.stats = stats || {};
        if (!self.loggedStats) {
          self.loggedStats = true;
          window.console.info('[personalised] stats response', stats);
        }
        self.pruneSelection();
        self.renderAll();
        self.checkStalled(campaign);
        if (self.queueOpen) { self.renderQueue(); }
        if (campaign.status === 'GENERATING') {
          self.startPolling();
        } else {
          self.stopPolling();
        }
      }).fail(function (xhr) {
        self.stopPolling();
        self.ui.body.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    /**
     * The worker picks one item at a time. If the item counts don't move for a while
     * during GENERATING, the worker is probably not running or keeps failing: say so.
     */
    checkStalled: function (campaign) {
      var counts = _.countBy(this.items, function (item) { return item.generationStatus; });
      var signature = _.map(['PENDING', 'GENERATING', 'DONE', 'FAILED'], function (key) {
        return counts[key] || 0;
      }).join('/');
      if (campaign.status !== 'GENERATING') {
        this.lastSignature = null;
        return;
      }
      if (signature !== this.lastSignature) {
        this.lastSignature = signature;
        this.unchangedSince = Date.now();
        return;
      }
      var seconds = Math.round((Date.now() - this.unchangedSince) / 1000);
      if (seconds >= STALL_SECONDS) {
        var message = 'No progress for ' + seconds + ' s (pending/generating/done/failed = ' + signature +
          '). The generation worker may not be running — check the server log.';
        window.console.warn('[personalised] ' + message);
        Shell.banner(this.$el, message, 'error');
      }
    },

    startPolling: function () {
      var self = this;
      if (this.pollTimer) { return; }
      this.pollTimer = window.setInterval(function () { self.load(); }, POLL_MS);
    },

    stopPolling: function () {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    //----------Rendering----------------------------------------------------//

    isSendable: function (item) {
      return item.generationStatus === 'DONE' && !item.sent;
    },

    canSend: function () {
      return this.campaign.status !== 'DRAFT';
    },

    /** A refresh can turn a selected item into a sent one; don't keep it ticked. */
    pruneSelection: function () {
      var self = this;
      var kept = {};
      _.each(this.items, function (item) {
        if (self.selected[item.id] && self.isSendable(item)) { kept[item.id] = true; }
      });
      this.selected = kept;
    },

    renderAll: function () {
      var campaign = this.campaign;
      var items = this.items;
      var counts = _.countBy(items, function (item) { return item.generationStatus; });
      var sentCount = _.filter(items, function (item) { return item.sent; }).length;
      var status = campaign.status || 'DRAFT';

      var queued = (counts.PENDING || 0) + (counts.GENERATING || 0);
      var actions =
        (queued ? '<button type="button" class="mlg-btn mlg-show-queue" title="Images waiting to be generated">' +
          'Queue (' + queued + ')</button>' : '') +
        (counts.FAILED ? '<button type="button" class="mlg-btn mlg-retry-failed" title="Put every failed ' +
          'image back in the generation queue — one OpenAI call each">Retry all failed (' + counts.FAILED + ')</button>' : '') +
        '<button type="button" class="mlg-btn mlg-test-image" title="Try the brief on any website before ' +
          'generating for the whole segment">Test image</button>' +
        (status === 'DRAFT' ?
          '<button type="button" class="mlg-btn primary mlg-generate">Start generation</button>' : '') +
        (status !== 'DRAFT' && this.newContacts > 0 ?
          '<button type="button" class="mlg-btn mlg-add-new-contacts" title="Contacts that joined the segment since ' +
            'the campaign started: their images are generated, nothing is sent">Add new contacts (' +
            Shell.formatNumber(this.newContacts) + ')</button>' : '') +
        (this.canSend() ?
          '<button type="button" class="mlg-btn primary mlg-send-selected" disabled>Send selected</button>' : '');

      var tiles = _.map([
        ['Recipients', items.length],
        ['Pending', counts.PENDING || 0],
        ['Generating', counts.GENERATING || 0],
        ['Ready', counts.DONE || 0],
        ['Failed', counts.FAILED || 0],
        ['Sent', sentCount]
      ], function (tile) {
        return '<div class="mlg-tile"><div class="t-label">' + tile[0] + '</div>' +
          '<div class="t-value">' + Shell.formatNumber(tile[1]) + '</div></div>';
      }).join('');

      var brief = _.map([
        ['Template', campaign.templateName],
        ['Segment', campaign.segmentName],
        ['Product', campaign.productType],
        ['Occasion', campaign.occasion],
        ['Text language', campaign.designLanguage],
        ['Subject', campaign.subject],
        ['Start sending', campaign.scheduledAt ? Shell.formatDate(campaign.scheduledAt, true) : 'As soon as sent'],
        ['Sending window', Shell.sendWindowSummary(campaign)],
        ['Occasion details', campaign.occasionDetails],
        ['Mandatory text', campaign.mandatoryText]
      ], function (line) {
        return '<div class="b-line"><span class="b-key">' + line[0] + '</span>' +
          '<span class="b-value">' + _.escape(line[1] || '—') + '</span></div>';
      }).join('');

      this.ui.body.html(
        '<div class="mlg-review-head">' +
        '  <div><h2>' + _.escape(campaign.name || '') + '</h2>' +
        '  <span class="mlg-chip st-' + (STATUS_CLASS[status] || 'draft') + '">' + Shell.prettyCase(status) + '</span>' +
        (status === 'GENERATING' ? ' <span class="mlg-hint inline">One image at a time, 30–90 s each — this page ' +
          'refreshes by itself.</span>' : '') +
        '  </div>' +
        '  <div class="mlg-review-actions">' + actions + '</div>' +
        '</div>' +
        this.multiAddressHtml() +
        '<div class="mlg-tiles">' + tiles + '</div>' +
        this.performanceHtml() +
        '<div class="mlg-settings-card mlg-brief">' +
        '  <h3>Brief <button type="button" class="mlg-btn small mlg-edit-schedule">Edit sending schedule</button></h3>' +
        '  <div class="b-grid">' + brief + '</div>' +
        (campaign.baseImageUrl ?
          '<div class="b-image"><img src="' + _.escape(campaign.baseImageResolvedUrl || campaign.baseImageUrl) +
            '" alt="Base product">' +
          '<div class="mlg-hint">' + _.escape(campaign.baseImageUrl) + '</div></div>' : '') +
        '</div>' +
        this.imagePromptCardHtml() +
        this.proofsheetCardHtml() +
        this.itemsHtml());
      this.refreshSendButton();
    },

    //----------Proofsheet button---------------------------------------------//

    /**
     * Recipients whose contact address is a list ("a@x.com; b@y.com", left by
     * the old contact sync): their email fails. Shown only when there are some.
     */
    multiAddressHtml: function () {
      var count = this.campaign.multiAddressCount || 0;
      if (!count) {
        return '';
      }
      return '<div class="mlg-settings-card mlg-multi-address">' +
        '<div><b>' + Shell.formatNumber(count) + ' recipient(s) have several addresses in one field</b> ' +
        '(e.g. "a@x.com; b@y.com"): their email fails, or would.</div>' +
        '<div class="mlg-hint">The fix cancels their failed or waiting emails in the outbox, and turns each of them ' +
          'into one recipient per address, with the image already generated (no new OpenAI call). Then select them ' +
          'and send as usual.</div>' +
        '<button type="button" class="mlg-btn primary mlg-fix-multi-address">Fix multi-address recipients</button>' +
        '</div>';
    },

    onFixMultiAddress: function () {
      var count = this.campaign.multiAddressCount || 0;
      if (!window.confirm('Split the ' + count + ' recipient(s) with several addresses into one recipient per address?\n\n' +
          'Their failed or waiting emails are cancelled first. Nothing is sent: select the new recipients and send them.')) {
        return;
      }
      var self = this;
      MailingApi.fixPersonalisedMultiAddress(this.secId).done(function (result) {
        Shell.banner(self.$el, (result.split || 0) + ' recipient(s) split into ' + (result.created || 0) +
          ' — ' + (result.cancelled || 0) + ' email(s) cancelled in the outbox' +
          (result.skipped ? ', ' + result.skipped + ' left as they are (image being generated, or no valid address)' : '') +
          '. Select them and send.', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    imagePromptCardHtml: function () {
      var campaign = this.campaign;
      var custom = !!campaign.imagePrompt;
      var draft = (campaign.status || 'DRAFT') === 'DRAFT';
      var older = campaign.olderPromptCount || 0;
      return '<div class="mlg-settings-card mlg-brief mlg-image-prompt-card">' +
        '<h3>Image prompt ' +
          '<button type="button" class="mlg-btn small mlg-image-prompt-edit">Edit prompt…</button>' +
          (older ? ' <button type="button" class="mlg-btn small primary mlg-regenerate-older" ' +
            'title="One paid OpenAI call per image">Regenerate with new prompt (' + Shell.formatNumber(older) +
            ')</button>' : '') +
        '</h3>' +
        '<div class="b-grid"><div class="b-line"><span class="b-key">Prompt</span><span class="b-value">' +
          (custom ? 'Custom prompt — this campaign only' : 'Built-in prompt') + '</span></div>' +
          (older ? '<div class="b-line"><span class="b-key">Older prompt</span><span class="b-value">' +
            Shell.formatNumber(older) + ' image(s) still to send were made with an earlier version</span></div>' : '') +
        '</div>' +
        (custom ? '<details class="mlg-advanced"><summary>Show the prompt</summary>' +
          '<pre class="mlg-prompt-text">' + _.escape(campaign.imagePrompt) + '</pre></details>' : '') +
        '<div class="mlg-hint">' + (draft ?
          'Try a change with "Test image" before generating: it uses the saved prompt.' :
          'A change applies to every image generated from then on: waiting ones, retries, regenerations. ' +
          'Emails already sent or in the outbox keep their image.') + '</div>' +
        '</div>';
    },

    onEditImagePrompt: function () {
      var self = this;
      this.ui.modal.html(
        '<div class="mlg-modal-card wide">' +
        '<h3>Image prompt</h3>' +
        ((this.campaign.status || 'DRAFT') !== 'DRAFT' ?
          '<div class="mlg-hint warn">Images already generated keep the prompt they were made with: once saved, ' +
            'they are marked "Older prompt" and can be regenerated in one click.</div>' : '') +
        Shell.imagePromptHtml() +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-image-prompt-save">Save</button>' +
        '</div></div>').show();
      MailingApi.defaultPersonalisedImagePrompt().done(function (result) {
        Shell.bindImagePrompt(self.ui.modal, self.campaign.imagePrompt, (result || {}).prompt);
      }).fail(function (xhr) {
        Shell.bindImagePrompt(self.ui.modal, self.campaign.imagePrompt, '');
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onSaveImagePrompt: function () {
      var self = this;
      MailingApi.updatePersonalisedImagePrompt(this.secId, this.ui.modal.find('.f-image-prompt').val())
        .done(function (campaign) {
          var older = (campaign || {}).olderPromptCount || 0;
          self.closeModal();
          Shell.banner(self.$el, 'Image prompt saved' + (older ? ' — ' + older + ' image(s) still to send were ' +
            'made with the previous one: "Regenerate with new prompt" redoes them.' : ''), 'ok');
          self.load();
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
    },

    proofsheetCardHtml: function () {
      var campaign = this.campaign;
      var configured = !!campaign.proofsheetProductRef;
      var summary = '';
      if (configured) {
        summary = '<div class="b-grid">' +
          '<div class="b-line"><span class="b-key">Product</span><span class="b-value">' +
            _.escape(ProofsheetProduct.summary(campaign.proofsheetProductName || campaign.proofsheetProductRef,
              campaign.proofsheetConfiguration)) + '</span></div>' +
          '<div class="b-line"><span class="b-key">Offers created</span><span class="b-value">' +
            Shell.formatNumber(campaign.proofsheetCount || 0) + '</span></div>' +
          '</div>';
      }
      return '<div class="mlg-settings-card mlg-brief mlg-proofsheet">' +
        '<h3>Proofsheet button ' +
          '<button type="button" class="mlg-btn small mlg-proofsheet-choose">' +
            (configured ? 'Change product…' : 'Choose product…') + '</button>' +
          (configured ? ' <button type="button" class="mlg-btn small danger mlg-proofsheet-remove">Remove</button>' : '') +
        '</h3>' +
        (configured ? summary :
          '<div class="mlg-hint">Optional, PIM products only. Configure a product and add a button to the template whose link is ' +
            '<code>{{proofsheet_url}}</code>: a recipient who clicks it (and confirms — link scanners do not) gets ' +
            'an offer with this configuration, their personalised image as its proofsheet.</div>') +
        (configured ? '<div class="mlg-hint">In the template, the button\'s link must be <code>{{proofsheet_url}}</code>. ' +
          'Changing the product only affects the offers created from now on.</div>' : '') +
        '</div>';
    },

    onChooseProofsheetProduct: function () {
      var self = this;
      ProofsheetProduct.choose(null).done(function (product) {
        MailingApi.configurePersonalisedProofsheet(self.secId, product).done(function () {
          Shell.banner(self.$el, 'Proofsheet product saved', 'ok');
          self.load();
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
      });
    },

    onRemoveProofsheetProduct: function () {
      var self = this;
      if (!window.confirm('Remove the proofsheet product? Recipients who click the button from now on see "no ' +
          'longer available"; the offers already created stay.')) {
        return;
      }
      MailingApi.configurePersonalisedProofsheet(this.secId, { proofsheetProductRef: '' }).done(function () {
        Shell.banner(self.$el, 'Proofsheet product removed', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onOpenOffer: function (e) {
      e.preventDefault();
      App.navigate('orders/' + $(e.currentTarget).data('poi'), { trigger: true });
    },

    proofsheetCellHtml: function (item) {
      if (item.proofsheetPoi) {
        return '<div class="c-sub"><a href="#orders/' + _.escape(item.proofsheetPoi) + '" class="mlg-proofsheet-open" ' +
          'data-poi="' + _.escape(item.proofsheetPoi) + '" title="Offer created from the proofsheet button">' +
          'Offer ' + _.escape(item.proofsheetPoi) + '</a>' +
          (item.proofsheetUrl ? ' · <a href="' + _.escape(item.proofsheetUrl) + '" target="_blank" ' +
            'rel="noopener noreferrer" title="' + _.escape(item.proofsheetUrl) + '">customer page ↗</a>' : '') +
          '</div>' +
          // The offer exists, but something after its creation went wrong (the image as custom proofsheet).
          (item.proofsheetError ? '<div class="c-sub error" title="' + _.escape(item.proofsheetError) + '">' +
            _.escape(item.proofsheetError) + '</div>' : '');
      }
      if (item.proofsheetStatus === 'CREATING') {
        return '<div class="c-sub">Offer being created…</div>';
      }
      if (item.proofsheetStatus === 'FAILED') {
        return '<div class="c-sub error" title="' + _.escape(item.proofsheetError || '') + '">Offer failed: ' +
          _.escape(item.proofsheetError || '') + '</div>';
      }
      return '';
    },

    /** First defined value among the keys the backends have used for the same figure. */
    statOf: function (keys) {
      var stats = this.stats || {};
      var key = _.find(keys, function (k) { return stats[k] !== undefined && stats[k] !== null; });
      return Number(stats[key]) || 0;
    },

    performanceHtml: function () {
      var sentItems = _.filter(this.items, function (item) { return item.sent; }).length;
      var sent = this.statOf(['sent', 'total', 'sentItems']) || sentItems;
      if (!sent) { return ''; }
      var self = this;
      return '<div class="mlg-settings-card mlg-performance"><h3>Email performance</h3>' +
        '<div class="mlg-tiles perf">' + _.map([
        ['Sent', sent, false],
        ['Opened', self.statOf(['opened', 'uniqueOpens', 'opens']), true],
        ['Clicked', self.statOf(['clicked', 'uniqueClicks', 'clicks']), true],
        ['Bounced', self.statOf(['bounced', 'failed']), true],
        ['Unsubscribed', self.statOf(['unsubscribed', 'unsubscribes', 'unsubs']), true]
      ], function (tile) {
        var value = tile[1];
        return '<div class="mlg-tile"><div class="t-label">' + tile[0] + '</div>' +
          '<div class="t-value">' + Shell.formatNumber(value) + '</div>' +
          (tile[2] ? '<div class="t-sub">' + Shell.formatPercent(value / sent) + '</div>' : '') +
          '</div>';
      }).join('') + '</div></div>';
    },

    //----------Sorting------------------------------------------------------//

    /** Sort keys → what to compare. Text keys sort case-insensitively, empty last. */
    SORT_VALUES: {
      id: function (item) { return item.id; },
      recipient: function (item) {
        return $.trim((item.lastName || '') + ' ' + (item.firstName || '') + ' ' + (item.email || '')).toLowerCase();
      },
      brand: function (item) {
        return (item.websiteUrl || '').replace(/^https?:\/\/(www\.)?/i, '').toLowerCase();
      },
      image: function (item) {
        // Ready first, then the states one waits on, failures last; sent ready ones after unsent.
        var rank = { DONE: 0, GENERATING: 1, PENDING: 2, FAILED: 3 }[item.generationStatus];
        return (rank === undefined ? 9 : rank) * 2 + (item.sent ? 1 : 0);
      }
    },

    sortedItems: function () {
      var value = this.SORT_VALUES[this.sort.key] || this.SORT_VALUES.id;
      var dir = this.sort.dir;
      return this.items.slice().sort(function (a, b) {
        var va = value(a);
        var vb = value(b);
        if (va === '' && vb !== '') { return 1; }   // empty last whatever the direction
        if (vb === '' && va !== '') { return -1; }
        if (va < vb) { return -dir; }
        if (va > vb) { return dir; }
        return a.id - b.id;
      });
    },

    onSort: function (e) {
      var key = $(e.currentTarget).data('sort');
      this.sort = key === this.sort.key ? { key: key, dir: -this.sort.dir } : { key: key, dir: 1 };
      this.ui.body.find('.mlg-items').replaceWith(this.itemsHtml());
      this.refreshSendButton();
    },

    sortableHeader: function (key, label) {
      var active = this.sort.key === key;
      return '<th data-sort="' + key + '" class="sortable' + (active ? ' active' : '') + '" title="Sort by ' +
        _.escape(label.toLowerCase()) + '">' + _.escape(label) +
        (active ? '<span class="s-arrow">' + (this.sort.dir === 1 ? '▲' : '▼') + '</span>' : '') + '</th>';
    },

    itemsHtml: function () {
      var self = this;
      if (!this.items.length) {
        return Shell.emptyHtml(this.campaign.status === 'DRAFT' ?
          'Nothing generated yet. "Test image" tries the brief on one website; "Start generation" creates ' +
            'one image per contact of the segment.' :
          'This campaign has no recipient.');
      }
      var canSend = this.canSend();
      var rows = _.map(this.sortedItems(), function (item) {
        var sendable = self.isSendable(item);
        var status = item.generationStatus || 'PENDING';
        // Not while the job holds the row, not once the email left.
        var removable = status !== 'GENERATING' && !item.sent;
        var name = $.trim((item.firstName || '') + ' ' + (item.lastName || ''));
        if (status === 'FAILED' && !self.loggedFailures[item.id]) {
          self.loggedFailures[item.id] = true;
          window.console.error('[personalised] generation failed for ' + item.email + ':', item.generationError);
        }
        return '<tr data-secid="' + item.secId + '" data-id="' + item.id + '">' +
          (canSend ? '<td class="c-check"><input type="checkbox" class="mlg-select-item"' +
            (sendable ? '' : ' disabled') + (self.selected[item.id] ? ' checked' : '') + '></td>' : '') +
          '<td class="c-thumb">' + (item.generatedImageUrl ?
            '<img class="mlg-item-image" src="' + _.escape(Shell.thumbnailUrl(item.generatedImageUrl)) +
              '" alt="" title="Enlarge" loading="lazy">' :
            '<span class="mlg-thumb-empty g-' + status.toLowerCase() + '"></span>') + '</td>' +
          '<td class="c-name">' + _.escape(name || '(no name)') +
            (item.sent ? ' <span class="mlg-chip st-sent">Sent</span>' : '') +
            (item.olderPrompt ? ' <span class="mlg-chip st-older-prompt" title="Made with an earlier version of the ' +
              'image prompt">Older prompt</span>' : '') +
          '  <div class="c-sub">' + _.escape(item.email || '') + '</div>' +
          '</td>' +
          '<td>' + (item.websiteUrl ?
            '<a href="' + _.escape(item.websiteUrl) + '" target="_blank" rel="noopener noreferrer">' +
              _.escape(item.websiteUrl) + '</a>' :
            '<span class="mlg-hint inline">No company website</span>') + '</td>' +
          '<td><span class="mlg-gen g-' + status.toLowerCase() + '">' + GENERATION_LABEL[status] + '</span>' +
            (status === 'FAILED' && item.generationError ?
              '<div class="c-sub error" title="' + _.escape(item.generationError) + '">' +
                _.escape(item.generationError) + '</div>' : '') +
            self.proofsheetCellHtml(item) +
          '</td>' +
          '<td class="c-actions">' +
          '  <button type="button" class="mlg-btn small mlg-item-email">Preview email</button>' +
            (status === 'FAILED' ?
              '<button type="button" class="mlg-btn small mlg-item-retry">Retry</button>' : '') +
            (status !== 'GENERATING' ?
              '<button type="button" class="mlg-btn small mlg-item-regenerate" ' +
                'title="Generate another image — calls OpenAI again">Regenerate</button>' : '') +
            (removable ?
              '<button type="button" class="mlg-btn small danger mlg-item-remove" ' +
                'title="Take this recipient out of the campaign">Remove</button>' : '') +
          '</td></tr>';
      }).join('');

      return '<table class="mlg-table mlg-items">' +
        '<thead><tr>' +
        (canSend ? '<th class="c-check"><input type="checkbox" class="mlg-select-all" title="Select every ready image"></th>' : '') +
        '<th></th>' + this.sortableHeader('recipient', 'Recipient') +
        this.sortableHeader('brand', 'Brand looked up') + this.sortableHeader('image', 'Image') + '<th></th>' +
        '</tr></thead><tbody>' + rows + '</tbody></table>';
    },

    //----------Selection----------------------------------------------------//

    onSelectItem: function (e) {
      var $box = $(e.currentTarget);
      var id = $box.closest('tr').data('id');
      if ($box.is(':checked')) {
        this.selected[id] = true;
      } else {
        delete this.selected[id];
      }
      this.refreshSendButton();
    },

    onSelectAll: function (e) {
      var self = this;
      var checked = $(e.currentTarget).is(':checked');
      this.selected = {};
      if (checked) {
        _.each(this.items, function (item) {
          if (self.isSendable(item)) { self.selected[item.id] = true; }
        });
      }
      this.ui.body.find('.mlg-select-item:not(:disabled)').prop('checked', checked);
      this.refreshSendButton();
    },

    refreshSendButton: function () {
      var count = _.size(this.selected);
      this.ui.body.find('.mlg-send-selected')
        .prop('disabled', !count)
        .text(count ? 'Send ' + count + ' email' + (count === 1 ? '' : 's') : 'Send selected');
    },

    //----------Actions------------------------------------------------------//

    onGenerate: function () {
      var self = this;
      var audience = this.campaign.segmentCount ?
          'about ' + Shell.formatNumber(this.campaign.segmentCount) + ' contacts' : 'every contact of the segment';
      if (!window.confirm('Generate one image for ' + audience + '?\n\n' +
          'Each image is a paid OpenAI call. Use "Test image" first if the brief is new.')) {
        return;
      }
      MailingApi.generatePersonalisedCampaign(this.secId).done(function (result) {
        Shell.banner(self.$el, 'Generation started for ' + Shell.formatNumber(result.total) + ' recipient(s)', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onSend: function () {
      var self = this;
      var ids = _.map(_.keys(this.selected), Number);
      if (!ids.length) { return; }
      if (!window.confirm('Queue ' + ids.length + ' personalised email(s) for sending?\n\nThey only leave ' +
          Shell.sendWindowSummary(this.campaign) +
          (this.campaign.scheduledAt ? ', starting ' + Shell.formatDate(this.campaign.scheduledAt, true) : '') + '.')) { return; }

      MailingApi.sendPersonalisedItems(this.secId, ids).done(function (result) {
        self.selected = {};
        Shell.banner(self.$el, (result.queued || 0) +
          ' email(s) queued. They leave the outbox at the configured rate.', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onRetry: function (e) {
      this.requeue(MailingApi.retryPersonalisedItem(this.secId, this.itemSecIdOf(e)), 'Retrying generation');
    },

    onRegenerateOlderPrompt: function () {
      var older = this.campaign.olderPromptCount || 0;
      if (!window.confirm('Regenerate the ' + older + ' image(s) made with an earlier prompt? Their current image ' +
          'is discarded and each one is a paid OpenAI call.\n\nEmails already sent or in the outbox are not touched.')) {
        return;
      }
      var self = this;
      MailingApi.regeneratePersonalisedOlderPrompt(this.secId).done(function (result) {
        Shell.banner(self.$el, (result.requeued || 0) + ' image(s) back in the queue with the new prompt', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onRetryAllFailed: function () {
      var failed = _.filter(this.items, function (item) { return item.generationStatus === 'FAILED'; }).length;
      if (!window.confirm('Retry the ' + failed + ' failed image(s)? Each one is a paid OpenAI call.\n\n' +
          'Fix the cause first (missing company website on the contact, API key…) or they will fail again.')) {
        return;
      }
      var self = this;
      MailingApi.retryAllPersonalisedFailed(this.secId).done(function (result) {
        Shell.banner(self.$el, (result.requeued || 0) + ' image(s) back in the queue', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onRegenerate: function (e) {
      var item = this.itemOf(e);
      var note = item && item.sent ? '\n\nThis email was already sent; it keeps the old image.' : '';
      if (!window.confirm('Generate a new image for this recipient? The current one is discarded and OpenAI ' +
          'is called again.' + note)) {
        return;
      }
      this.requeue(MailingApi.regeneratePersonalisedItem(this.secId, this.itemSecIdOf(e)), 'Regeneration queued');
    },

    onRemove: function (e) {
      var item = this.itemOf(e);
      var who = item.email || 'this recipient';
      var note = item.generationStatus === 'DONE' ? ' Their generated image is dropped.' :
          item.generationStatus === 'PENDING' ? ' No image will be generated for them.' : '';
      if (!window.confirm('Remove ' + who + ' from this campaign?' + note + '\n\nNo email will be sent to them.')) {
        return;
      }
      delete this.selected[item.id];
      this.requeue(MailingApi.removePersonalisedItem(this.secId, item.secId), who + ' removed from the campaign');
    },

    requeue: function (request, message) {
      var self = this;
      request.done(function () {
        Shell.banner(self.$el, message, 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    //----------Modals-------------------------------------------------------//

    /** The recipients with an image, in the table's current order — what the arrows walk through. */
    itemsWithImage: function () {
      return _.filter(this.sortedItems(), function (item) { return !!item.generatedImageUrl; });
    },

    onShowImage: function (e) {
      var secId = this.itemSecIdOf(e);
      // No _.findIndex in this lodash build.
      var index = _.indexOf(_.map(this.itemsWithImage(), function (item) { return item.secId; }), secId);
      if (index !== -1) { this.showImageAt(index); }
    },

    onPrevImage: function () { this.showImageAt(this.imageIndex - 1); },
    onNextImage: function () { this.showImageAt(this.imageIndex + 1); },

    showImageAt: function (index) {
      var images = this.itemsWithImage();
      if (index < 0 || index >= images.length) { return; }   // ends stop, no wrap-around
      var item = images[index];
      this.imageIndex = index;
      var name = $.trim((item.firstName || '') + ' ' + (item.lastName || ''));
      this.ui.modal.html(
        '<div class="mlg-modal-card mlg-image-card">' +
        '<h3>' + _.escape(item.email || '') +
          '<span class="mlg-image-pos">' + (index + 1) + ' / ' + images.length + '</span></h3>' +
        '<div class="mlg-image-nav">' +
        '  <button type="button" class="mlg-btn mlg-image-prev" title="Previous (←)"' +
             (index === 0 ? ' disabled' : '') + '>&larr;</button>' +
        '  <img src="' + _.escape(item.generatedImageUrl) + '" alt="Generated image">' +
        '  <button type="button" class="mlg-btn mlg-image-next" title="Next (→)"' +
             (index === images.length - 1 ? ' disabled' : '') + '>&rarr;</button>' +
        '</div>' +
        '<div class="mlg-hint">' + _.escape(name) + (item.websiteUrl ? ' · Brand: ' + _.escape(item.websiteUrl) : '') +
          (item.sent ? ' · Sent' : '') + ' — ← → to browse, Esc to close</div>' +
        '<div class="mlg-modal-actions">' +
        '  <a class="mlg-btn" href="' + _.escape(item.generatedImageUrl) + '" target="_blank" ' +
             'rel="noopener noreferrer">Open full size</a>' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
        '</div>' +
        '</div>').show();
    },

    onShowEmail: function (e) {
      var item = this.itemOf(e);
      this.ui.modal.html(
        '<div class="mlg-modal-card wide mlg-preview-card">' +
        '<h3>Email for ' + _.escape(item.email || '') + '</h3>' +
        (item.generatedImageUrl ? '' :
          '<div class="mlg-hint">No image yet — the base product image stands in.</div>') +
        '<div class="mlg-preview-host"><div class="mlg-loading">Loading preview…</div></div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
        '</div>' +
        '</div>').show();
      var $host = this.ui.modal.find('.mlg-preview-host');
      MailingApi.previewPersonalisedItem(this.secId, item.secId).done(function (result) {
        Shell.showEmailHtml($host, (result || {}).html);
      }).fail(function (xhr) {
        $host.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    /** Generation queue: the worker takes one image at a time, oldest recipient first. */
    onShowQueue: function () {
      this.queueOpen = true;
      this.renderQueue();
    },

    renderQueue: function () {
      var waiting = _.filter(this.items, function (item) { return item.generationStatus === 'PENDING'; });
      var running = _.filter(this.items, function (item) { return item.generationStatus === 'GENERATING'; });
      waiting = _.sortBy(waiting, 'id');
      var rows = _.map(running.concat(waiting), function (item, index) {
        var isRunning = item.generationStatus === 'GENERATING';
        return '<tr data-secid="' + item.secId + '"><td>' + (isRunning ? '▶' : (index - running.length + 1)) + '</td>' +
          '<td>' + _.escape(item.email || '') +
          (item.websiteUrl ? '<div class="c-sub">' + _.escape(item.websiteUrl) + '</div>' : '') + '</td>' +
          '<td><span class="mlg-gen g-' + item.generationStatus.toLowerCase() + '">' +
            GENERATION_LABEL[item.generationStatus] + '</span></td>' +
          '<td>' + (isRunning ? '' :
            '<button type="button" class="mlg-btn small mlg-queue-remove" ' +
              'title="Remove this recipient from the campaign">Remove</button>') + '</td></tr>';
      }).join('');
      this.ui.modal.html(
        '<div class="mlg-modal-card wide">' +
        '<h3>Generation queue</h3>' +
        '<div class="mlg-hint">One image at a time (30–90 s each), oldest first. Updates by itself while ' +
          'the campaign is generating.</div>' +
        (rows ?
          '<table class="mlg-table"><thead><tr><th>#</th><th>Recipient</th><th>Status</th><th></th></tr></thead>' +
            '<tbody>' + rows + '</tbody></table>' :
          '<div class="mlg-hint">The queue is empty.</div>') +
        '<div class="mlg-modal-actions">' +
        (waiting.length ?
          '  <button type="button" class="mlg-btn mlg-queue-clear">Remove all waiting (' + waiting.length + ')</button>' : '') +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
        '</div></div>').show();
    },

    onQueueRemove: function (e) {
      var secId = $(e.currentTarget).closest('tr').data('secid');
      if (!window.confirm('Remove this recipient from the campaign? No image will be generated and no email sent to them.')) {
        return;
      }
      this.requeue(MailingApi.removePersonalisedItem(this.secId, secId), 'Recipient removed from the queue');
    },

    onQueueClear: function () {
      if (!window.confirm('Remove every recipient still waiting in the queue from the campaign?')) { return; }
      this.requeue(MailingApi.clearPersonalisedQueue(this.secId), 'Waiting recipients removed');
    },

    onEditSchedule: function () {
      var start = this.campaign.scheduledAt ? Shell.toLocalInputValue(new Date(this.campaign.scheduledAt)) : '';
      this.ui.modal.html(
        '<div class="mlg-modal-card">' +
        '<h3>Sending schedule</h3>' +
        '<label>Start sending on <span class="mlg-optional">optional — empty = as soon as you send</span>' +
          '<input type="datetime-local" class="f-start-at" value="' + start + '"></label>' +
        Shell.sendWindowHtml(this.campaign) +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-schedule-save">Save</button>' +
        '</div></div>').show();
    },

    onSaveSchedule: function () {
      var self = this;
      var window_ = Shell.readSendWindow(this.ui.modal);
      if (window_.error) {
        Shell.banner(this.$el, window_.error, 'error');
        return;
      }
      var startAt = this.ui.modal.find('.f-start-at').val();
      var data = _.extend({ scheduledAt: startAt ? new Date(startAt).getTime() : null }, window_.data);
      MailingApi.updatePersonalisedCampaign(this.secId, data).done(function () {
        self.closeModal();
        Shell.banner(self.$el, 'Sending schedule saved', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onOpenTest: function () {
      this.ui.modal.html(
        '<div class="mlg-modal-card mlg-image-card">' +
        '<h3>Test image generation</h3>' +
        '<div class="mlg-hint">Generates one image for any company website with this campaign\'s brief. ' +
          'No recipient is touched. One paid OpenAI call per test.</div>' +
        '<div class="mlg-row">' +
        '  <label class="grow"><input type="text" class="f-test-url" placeholder="https://www.example.com"></label>' +
        '  <button type="button" class="mlg-btn primary mlg-test-run">Generate</button>' +
        '</div>' +
        '<div class="mlg-test-result"></div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
        '</div>' +
        '</div>').show();
      this.ui.modal.find('.f-test-url').focus();
    },

    onTestKey: function (e) {
      if (e.which === 13) {
        e.preventDefault();
        this.onRunTest();
      }
    },

    onRunTest: function () {
      var $modal = this.ui.modal;
      var $run = $modal.find('.mlg-test-run');
      var $result = $modal.find('.mlg-test-result');
      var url = $.trim($modal.find('.f-test-url').val());
      if (!url || $run.prop('disabled')) { return; }

      $run.prop('disabled', true).text('Generating…');
      $result.html('<div class="mlg-loading">Browsing the website and generating the image — ' +
        'this takes 30 to 90 seconds.</div>');
      MailingApi.testPersonalisedImage(this.secId, url).done(function (result) {
        $result.html('<img src="' + _.escape(result.imageUrl) + '" alt="Test result">' +
          '<a href="' + _.escape(result.imageUrl) + '" target="_blank" rel="noopener noreferrer">Open full size</a>');
      }).fail(function (xhr) {
        $result.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      }).always(function () {
        $run.prop('disabled', false).text('Generate');
      });
    },

    closeModal: function () {
      this.queueOpen = false;
      this.imageIndex = undefined;
      this.ui.modal.hide().empty();
    },

    //----------Helpers------------------------------------------------------//

    itemSecIdOf: function (e) {
      // String(): jQuery's .data() would turn an all-digit value into a number.
      return String($(e.currentTarget).closest('tr').data('secid'));
    },

    itemOf: function (e) {
      return _.findWhere(this.items, { secId: this.itemSecIdOf(e) }) || {};
    },

    onBack: function (e) {
      e.preventDefault();
      App.navigate('mailing/personalised', { trigger: true });
    }
  });

  module.exports = ReviewView;
});

